import { useQuery } from "@apollo/client";
import { BaseTable, Flexbox, Modal } from "@sede-x/shell-ds-react-framework";
import { getPaginationRowModel } from "@tanstack/react-table";
import { loader } from "graphql.macro";
import { useState } from "react";
import { ApolloErrorViewer } from "shared/components/ApolloErrorViewer";
import LoadingPanel from "shared/components/LoadingPanel";
import { GqlResponse, TBatchRevision } from "ticketing/ticketing.types";

const lookupBatches = loader("../../ticketing-graphql/batchRevisions.graphql");

type Response = GqlResponse<TBatchRevision[], "batchRevisions">;

const transformData = (data: TBatchRevision[]): TBatchRevision[] => {
  return data
    .map(b => ({
      ...b,
      lastModifiedDate: new Date(`${b.lastModifiedDate}Z`)
    }))
    .sort((a, b) => b.revisionNumber.localeCompare(a.revisionNumber));
};
interface CellComponentProps {
  row: {
    original: TBatchRevision;
  };
}

const LastModifiedByCell1 = ({ row }: CellComponentProps) => {
  const item = row.original;
  const displayName = [item.lastModifiedBy?.lastName, item.lastModifiedBy?.firstName]
    .filter(Boolean)
    .join(", ");

  return <span title={item.lastModifiedBy?.id}>{displayName}</span>;
};

const TransitCompleteCell1 = ({ row }: CellComponentProps) => {
  const item = row.original;
  const style = {
    backgroundColor: item.transitComplete ? "rgba(55, 180, 0, 0.32)" : undefined,
    padding: "4px 8px",
    borderRadius: "4px",
    display: "inline-block"
  };

  return <span style={style}>{item.transitComplete ? "True" : "False"}</span>;
};

const columns = [
  {
    header: "Batch",
    accessorKey: "name",
    id: "name"
  },
  {
    header: "Revision Date/Time",
    accessorKey: "lastModifiedDate",
    id: "lastModifiedDate",

    cell: ({ getValue }: { getValue: () => string }) =>
      new Date(getValue()).toLocaleString("en-US", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false
      })
  },

  {
    header: "Updated By",
    accessorKey: "lastModifiedBy",
    id: "lastModifiedBy",
    cell: ({ row }: { row: { original: TBatchRevision } }) => <LastModifiedByCell1 row={row} />
  },
  {
    header: "Transit Complete",
    accessorKey: "transitComplete",
    id: "transitComplete",
    cell: ({ row }: { row: { original: TBatchRevision } }) => <TransitCompleteCell1 row={row} />
  }
];

type BatchRevisionsProps = {
  batchId: string;
  onClose: () => void;
};

/**
 *
 * @param param0
 * @returns
 */
const RevisionsBatch = ({ batchId, onClose }: BatchRevisionsProps) => {
  const { loading, error } = useQuery<Response>(lookupBatches, {
    fetchPolicy: "no-cache",
    variables: { id: batchId },
    onCompleted: data => setBatchRevisions(transformData(data.batchRevisions))
  });

  const [batchRevisions, setBatchRevisions] = useState<TBatchRevision[]>([]);

  return (
    <Modal
      open={true}
      width={700}
      title={`Revisions of Batch: ${batchId}`}
      onClose={onClose}
      bodyPadding={false}>
      <Flexbox
        flexDirection="column"
        style={{
          flex: 1,
          overflow: "auto"
        }}>
        {loading && <LoadingPanel />}
        {error && <ApolloErrorViewer error={error} />}

        {!loading && !error && (
          <BaseTable
            data={batchRevisions}
            columns={columns}
            tableOptions={{
              enableColumnResizing: true,
              columnResizeMode: "onChange",
              getPaginationRowModel: getPaginationRowModel()
            }}
            style={{
              width: "100%",
              tableLayout: "fixed"
            }}
          />
        )}
      </Flexbox>
    </Modal>
  );
};

export default RevisionsBatch;
